import { Button, ButtonIcon, ButtonText } from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import { Text } from "@repo/components/src/ui/text";
import { ArrowUpRight } from "lucide-react-native";
import React, { useCallback } from "react";
import type { SuggestionChip } from "./suggestionChipsData";

type Props = {
  label: string;
  /** Which chips this surface shows — pick a roster from `suggestionChipsData`. */
  chips: SuggestionChip[];
  /** Called with the chip's prompt — the caller starts the AI flow with it
   * as the opening message (chips are actions, not prefills). */
  onPick: (text: string) => void;
};

/** Per-position responsive visibility: the 4th chip needs `xl`, the 5th
 * `2xl` — capping beats wrapping. Literal classes so the Tailwind JIT sees
 * them. Rosters shorter than four chips are unaffected. */
const VISIBILITY_BY_INDEX = ["", "", "", "hidden xl:flex", "hidden 2xl:flex"];

/**
 * The suggestion-chip row under the composer, on the dark hero gradient —
 * frosted-glass pills (translucent white fill, white text) with a trailing
 * diagonal arrow signalling each chip executes an action. Skips the app's
 * standard `action`/`variant` button palette for that reason.
 */
const SuggestionChips = React.memo(({ label, chips, onPick }: Props) => {
  const handlePick = useCallback(
    (prompt: string) => () => onPick(prompt),
    [onPick],
  );

  return (
    <HStack space="sm" className="flex-wrap items-center">
      <Text className="mr-1 text-xs font-semibold text-brand-100">{label}</Text>
      {chips.map((chip, index) => (
        <Button
          key={chip.label}
          action="default"
          variant="outline"
          size="sm"
          rounded="full"
          onPress={handlePick(chip.prompt)}
          className={`border-white/[0.35] bg-white/[0.12] transition-colors duration-150 data-[hover=true]:border-white data-[hover=true]:bg-white/20 data-[active=true]:bg-white/20 ${
            VISIBILITY_BY_INDEX[index] ?? ""
          }`}
        >
          <ButtonText size="sm" className="font-semibold text-typography-white">
            {chip.label}
          </ButtonText>
          <ButtonIcon
            as={ArrowUpRight}
            className="h-[13px] w-[13px] text-typography-white"
          />
        </Button>
      ))}
    </HStack>
  );
});

SuggestionChips.displayName = "SuggestionChips";

export default SuggestionChips;
